import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { EtatService } from '../../Services/etat';
import { ParcService } from '../../Services/parc';
import {
  ParcContact,
  ParcContacts,
  ParcInformations,
} from '../../Services/parc-informations.types';

type CategorieContact = {
  cle: keyof ParcContacts;
  libelle: string;
  ouvert: ReturnType<typeof signal<boolean>>;
};

@Component({
  selector: 'app-parc-contacts',
  imports: [CommonModule, RouterLink],
  templateUrl: './parc-contacts.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parc-contacts.css',
})
export class ParcContactsComponent {
  private readonly etat = inject(EtatService);
  private readonly parcService = inject(ParcService);
  private numeroRequete = 0;
  private destroyRef = inject(DestroyRef);

  readonly informations = signal<ParcInformations | null>(null);
  readonly chargement = signal(false);
  readonly erreur = signal(false);

  readonly categories: CategorieContact[] = [
    { cle: 'maitres_ouvrage', libelle: "Maîtres d'ouvrage", ouvert: signal(false) },
    { cle: 'bureaux_etudes', libelle: "Bureaux d'études", ouvert: signal(false) },
    { cle: 'installateurs', libelle: 'Installateurs', ouvert: signal(false) },
    { cle: 'mainteneurs', libelle: 'Mainteneurs', ouvert: signal(false) },
  ];

  constructor() {
    effect(() => {
      const parc = this.etat.parcSelectionne();
      ++this.numeroRequete;
      if (!parc) {
        this.informations.set(null);
        this.chargement.set(false);
        this.erreur.set(false);
        return;
      }
      void this.chargerContacts(parc.id);
    });
  }

  contacts(cle: keyof ParcContacts): ParcContact[] {
    return this.informations()?.contacts[cle] ?? [];
  }

  nomComplet(contact: ParcContact): string {
    return [contact.prenom, contact.nom].filter(Boolean).join(' ') || contact.email;
  }

  private async chargerContacts(parcId: number): Promise<void> {
    const numeroRequete = ++this.numeroRequete;
    const estValide = () => numeroRequete === this.numeroRequete && !this.destroyRef.destroyed && parcId === this.etat.parcSelectionne()?.id;
    this.chargement.set(true);
    this.erreur.set(false);
    this.informations.set(null);
    this.categories.forEach((categorie) => categorie.ouvert.set(false));

    try {
      const informations = await this.parcService.getParc(parcId);
      if (!estValide()) return;
      this.informations.set(informations);
      this.erreur.set(informations === null);
    } catch {
      if (!estValide()) return;
      this.erreur.set(true);
    } finally {
      if (estValide()) this.chargement.set(false);
    }
  }
}
